import { asset } from '@/lib/assets'
import {
  CAMPAIGN_ICON_FEEDBACK,
  CAMPAIGN_ICON_FORUM,
  CAMPAIGN_ICON_MENU_BOOK,
  CAMPAIGN_ICON_TROPHY,
  CAMPAIGN_PAGE_BACKGROUND,
} from '@/components/campaign/sharedAssets'
import type { InterviewPracticeCampaignConfig } from '@/components/campaign/types'

const IMG_HERO = asset('/assets/images/data-analytics-engineering/hero-pipeline.png')
export const dataAnalyticsEngineeringCampaignConfig: InterviewPracticeCampaignConfig = {
  path: '/data-analytics-engineering',
  analyticsSource: 'data_analytics_engineering',
  analytics: {
    pageViewed: 'data_analytics_page_viewed',
    ctaClicked: 'data_analytics_cta_clicked',
    checkoutStarted: 'data_analytics_checkout_started',
  },
  idPrefix: 'dae',

  seo: {
    title: 'Data & Analytics Engineering Interview Practice',
    description:
      'Practice realistic data engineering interview rounds, catch mistakes early, and walk in confident with meritlens mock interviews and structured feedback.',
    openGraphTitle: 'Data & Analytics Engineering Interview Practice | meritlens',
    openGraphDescription:
      'Find your interview gaps before the real round. Realistic data pipeline practice based on your resume.',
    twitterDescription:
      'Practice realistic data engineering rounds and get structured feedback before your next interview.',
    heroImageForSharing: IMG_HERO,
    heroImageAlt: 'Data and analytics engineering interview practice with meritlens',
  },

  hero: {
    roleBadge: 'Data & Analytics Engineering',
    titleLine: 'Find Your Interview Gaps',
    titleAccent: 'Before the Real Round.',
    subtitle:
      "Just like you'd never deploy an unverified model, you should never face a technical round untested.",
    heroImageSrc: IMG_HERO,
    heroImageAlt: 'Data and analytics engineering interview practice with meritlens',
    caption:
      'Practice with realistic data engineering rounds, catch your mistakes early, and walk in confident to explain your designs.',
  },

  pageBackgroundImage: CAMPAIGN_PAGE_BACKGROUND,

  offer: {
    cardTitle: "Don't Let Small Mistakes Cost You the Offer",
    offerPreviewImage: asset('/assets/images/data-analytics-engineering/offer-card-preview.png'),
    microcopy: 'One practice session closer to your next career move.',
    bullets: [
      'Face realistic data pipeline challenges based directly on your resume',
      'Uncover technical blind spots with clear feedback before meeting the panel',
    ],
  },

  why: {
    intro:
      'Four ways the practice session helps you prepare. \nThe panels below are illustrative examples, not real candidate data.',
    features: [
      {
        icon: CAMPAIGN_ICON_FORUM,
        titleDesktop: 'Realistic Interview Practice',
        titleMobile: 'Realistic Interview Practice',
        description:
          'Experience interview-style questions and practise articulating your answers as \nyou would in an actual interview.',
      },
      {
        icon: CAMPAIGN_ICON_MENU_BOOK,
        titleDesktop: 'Put Your Technical Knowledge to the Test',
        titleMobile: 'Test your data engineering skills',
        description:
          'Work through questions that challenge your understanding, analytical thinking, \nand problem-solving skills.',
      },
      {
        icon: CAMPAIGN_ICON_FEEDBACK,
        titleDesktop: 'Feedback to Help You Improve',
        titleMobile: 'Know what to improve next',
        description:
          'Review structured feedback to understand your strengths and identify \nareas that need more preparation.',
      },
      {
        icon: CAMPAIGN_ICON_TROPHY,
        titleDesktop: 'Prepare Around Your Career Goals',
        titleMobile: 'Practice for your target role',
        description:
          'Choose resume-based practice to focus on your skills and experience, or use a job description to prepare for your target role.',
      },
    ],
  },
}
